import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function ShieldIcon({ size = 16 }: { size?: number }) {
    return (
        <svg
            viewBox='0 0 20 20'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.6'
            aria-hidden='true'
            width={size}
            height={size}
        >
            <path
                d='M10 2L3 5.5V10c0 4.1 3 7.6 7 8.5 4-1 7-4.4 7-8.5V5.5L10 2z'
                strokeLinejoin='round'
            />
            <path d='M7 10.5l2 2 4-4' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
    );
}

export function SecurityBadge() {
    const [open, setOpen] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        function handleKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open]);

    useEffect(() => {
        if (open) dialogRef.current?.focus();
    }, [open]);

    const modal =
        open &&
        // Portalled to document.body so the modal's z-index:300/301 lives in the
        // root stacking context rather than being clipped by the workspace-bg
        // stacking context (z:0) or the header stacking context (z:10).
        createPortal(
            <>
                <div className='modal-backdrop' onClick={() => setOpen(false)} aria-hidden='true' />
                <div
                    ref={dialogRef}
                    role='dialog'
                    aria-modal='true'
                    aria-labelledby='security-dialog-title'
                    className='modal-card'
                    tabIndex={-1}
                >
                    <div className='modal-header'>
                        <ShieldIcon size={18} />
                        <h2 id='security-dialog-title' className='modal-title'>
                            Data security
                        </h2>
                        <button
                            type='button'
                            className='modal-close'
                            aria-label='Close'
                            onClick={() => setOpen(false)}
                        >
                            ×
                        </button>
                    </div>
                    <div className='modal-body'>
                        <p className='modal-lead'>Your diagram stays in your browser.</p>
                        <p>
                            The Nowline Free editor runs entirely client-side. Your diagram is
                            rendered locally and is never uploaded to or stored on Nowline's
                            servers.
                        </p>
                        <p>
                            Share links carry the diagram inside the URL itself — everything after
                            the <code>#</code> — so it only ever travels where you choose to send
                            the link.
                        </p>
                    </div>
                </div>
            </>,
            document.body,
        );

    return (
        <>
            <button
                type='button'
                className='security-badge'
                title='Data security'
                aria-label='Data security'
                onClick={() => setOpen(true)}
            >
                <ShieldIcon size={15} />
            </button>

            {modal}
        </>
    );
}
